// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/extraGroups.ts
// 🔧 PURPOSE: Dove porta ogni riquadro del blocco "Aggiungi al tuo soggiorno"
//             della Home, e come si dividono i servizi nel negozio Extra.
//
//             I riquadri della Home sono FAMIGLIE di servizi, non servizi:
//             "Cibo & bevande" copre caffè & brioche, colazione, bottiglia di
//             benvenuto e aperitivo in terrazza, "Taxi & transfer" aeroporto e
//             stazione, "Scorte & comfort" carta, igiene, kit e biancheria. Un
//             ospite che tocca "Cibo & bevande" si aspetta di trovarci il
//             prosecco e il cornetto, non di ritrovarsi in un elenco di dodici
//             cose.
//
//             La mappa sta qui e non dentro i componenti perché la leggono
//             in quattro: la Home (dove va il tocco e con quale figura), la
//             schermata di gruppo (quali servizi mostrare), la schermata
//             singola (a quale gruppo tornare) e il negozio (in quale
//             sezione sta ogni servizio).
// ============================================

import type { AppExtra } from './guestApi';
import { cheapestItemPrice, listedSlots } from './extraSelection';

/** Una famiglia di servizi dietro un solo riquadro della Home. */
export interface ExtraGroup {
  /** Id del gruppo: è anche il pezzo di URL (/extras/group/:groupId) e la chiave i18n (extras.groups.<id>). */
  id: 'foodDrinks' | 'clean' | 'taxi' | 'supplies';
  /** I servizi, nell'ordine in cui si mostrano (il più venduto per primo). */
  serviceIds: string[];
  /** L'illustrazione del riquadro in Home (dentro l'app, funziona anche offline). */
  image: string;
}

/**
 * Le famiglie, per ID DI SERVIZIO e non per categoria.
 *
 * La categoria sarebbe stata più "automatica", ma `food` contiene sia la
 * colazione sia la bottiglia di benvenuto, e l'ospite le cerca in due posti
 * diversi. Un elenco esplicito dice esattamente cosa c'è dietro ogni
 * riquadro, e il giorno in cui nasce un servizio nuovo lo si aggiunge qui —
 * dove si vede — invece di scoprirlo comparso nel riquadro sbagliato.
 *
 * Sono QUATTRO, e non è un caso: in Home stanno in una griglia 2×2 piena,
 * senza riquadri spaiati. Cibo e bevande stanno insieme perché "mangiare e
 * bere" è un pensiero solo — l'ospite che cerca il cornetto e quello che
 * cerca il prosecco aprono lo stesso riquadro.
 */
export const EXTRA_GROUPS: ExtraGroup[] = [
  // Scorte & comfort per primo: è la famiglia che si compra durante il
  // soggiorno, quando qualcosa finisce davvero. Prima i kit già pronti.
  // Il detersivo per la lavatrice sta con le scorte: si compra quando serve.
  { id: 'supplies', serviceIds: ['comfort_kits', 'paper_supplies', 'hygiene_supplies', 'linen_supplies', 'laundry_supplies'], image: '/images/extras/supplies.svg' },
  // L'acqua sta accanto al vino: è la stessa voglia di "qualcosa da bere".
  { id: 'foodDrinks', serviceIds: ['caffe_brioche', 'tarallini', 'breakfast', 'welcome_wine', 'water_bottles', 'terrace_aperitivo'], image: '/images/extras/food-drinks.svg' },
  { id: 'clean', serviceIds: ['extra_cleaning'], image: '/images/extras/clean.svg' },
  { id: 'taxi', serviceIds: ['airport_transfer', 'station_transfer', 'custom_transfer'], image: '/images/extras/taxi.svg' },
];

/**
 * Gli id di gruppo di ieri. `food` e `drinks` sono stati riquadri veri, e i
 * loro link vivono ancora in notifiche, messaggi e cronologia del browser:
 * qui restano validi e portano alla famiglia che li ha assorbiti, invece di
 * rimbalzare l'ospite sul negozio.
 */
const LEGACY_GROUP_IDS: { [oldId: string]: ExtraGroup['id'] } = {
  food: 'foodDrinks',
  drinks: 'foodDrinks',
};

export const findExtraGroup = (groupId?: string): ExtraGroup | undefined => {
  const id = (groupId && LEGACY_GROUP_IDS[groupId]) || groupId;
  return EXTRA_GROUPS.filter((g) => g.id === id)[0];
};

/** Il gruppo a cui appartiene un servizio (per il "torna indietro"). */
export const groupOfService = (serviceId: string): ExtraGroup | undefined =>
  EXTRA_GROUPS.filter((g) => g.serviceIds.indexOf(serviceId) >= 0)[0];

/**
 * Rimette i servizi di una stessa famiglia nell'ordine della famiglia
 * (aeroporto, stazione, poi "taxi su misura"), lasciando tutto il resto
 * dov'era. Il negozio riceve gli extra nell'ordine del catalogo, e un
 * servizio nato dopo puo' avere un numero d'ordine che lo mette davanti
 * ai suoi fratelli: qui l'ordine lo decide l'elenco della famiglia.
 */
export const orderByGroup = (items: AppExtra[]): AppExtra[] => {
  const out = items.slice();
  for (let g = 0; g < EXTRA_GROUPS.length; g++) {
    const ids = EXTRA_GROUPS[g].serviceIds;
    const positions: number[] = [];
    for (let i = 0; i < out.length; i++) {
      if (ids.indexOf(out[i].serviceId) >= 0) positions.push(i);
    }
    if (positions.length < 2) continue;
    const ordered = positions
      .map((pos) => out[pos])
      .sort((a, b) => ids.indexOf(a.serviceId) - ids.indexOf(b.serviceId));
    for (let k = 0; k < positions.length; k++) out[positions[k]] = ordered[k];
  }
  return out;
};

export const extraDetailPath = (serviceId: string): string => `/extras/${serviceId}`;
export const extraGroupPath = (groupId: string): string => `/extras/group/${groupId}`;

/**
 * La destinazione di un riquadro della Home: un servizio solo → dritti alla
 * sua schermata; più servizi → la schermata di scelta del gruppo. La
 * schermata di gruppo salta comunque alla scheda quando, per QUESTA
 * prenotazione, un solo servizio della famiglia è in catalogo.
 */
export const groupTarget = (group: ExtraGroup): string =>
  group.serviceIds.length === 1 ? extraDetailPath(group.serviceIds[0]) : extraGroupPath(group.id);

/**
 * Le sezioni del negozio, nell'ordine in cui si leggono. `stay` è "Arrivo e
 * partenza": check-in anticipato, check-out posticipato e deposito bagagli,
 * le cose che si comprano pensando agli orari del viaggio.
 */
export type StoreSection = 'food' | 'drinks' | 'stay' | 'home' | 'transfer' | 'rental';
export const STORE_SECTIONS: StoreSection[] = ['food', 'drinks', 'stay', 'home', 'transfer', 'rental'];

/**
 * Nel negozio "Cibo" e "Bevande" restano due scaffali distinti anche se in
 * Home sono un riquadro solo: chi scorre il listino cerca da bere sotto
 * "Bevande". La famiglia non basta più a dirlo (ne contiene quattro), e la
 * categoria del server nemmeno (è `food` per tutto ciò che si mangia e si
 * beve): lo scaffale di questi servizi è scritto qui, servizio per
 * servizio.
 */
const STORE_SECTION_BY_SERVICE: { [serviceId: string]: StoreSection } = {
  caffe_brioche: 'food',
  tarallini: 'food',
  breakfast: 'food',
  welcome_wine: 'drinks',
  water_bottles: 'drinks',
  terrace_aperitivo: 'drinks',
  // Arrivo e partenza: la categoria del server non basta (il deposito
  // bagagli è `other`, come la pulizia), quindi anche qui servizio per servizio.
  early_checkin: 'stay',
  late_checkout: 'stay',
  luggage_storage: 'stay',
};

/**
 * In quale sezione del negozio sta un servizio. Arrivo e partenza hanno la
 * loro sezione (vedi sopra); tutto ciò che è "in appartamento" sta sotto
 * "Casa", perché per l'ospite è la stessa cosa: comodità del soggiorno.
 */
export const storeSectionOf = (item: Pick<AppExtra, 'serviceId' | 'category'>): StoreSection => {
  const known = STORE_SECTION_BY_SERVICE[item.serviceId];
  if (known) return known;
  const group = groupOfService(item.serviceId);
  if (item.category === 'food') return 'food';
  // Scorte e kit stanno in "Casa" come la pulizia extra: per l'ospite sono
  // tutte comodità dell'appartamento.
  if (group?.id === 'supplies') return 'home';
  if (item.category === 'transport') return 'transfer';
  if (item.category === 'rental') return 'rental';
  return 'home';
};

/** Il prezzo "da" di una famiglia: il più basso fra i servizi in catalogo. */
export const groupStartingPrice = (group: Pick<ExtraGroup, 'serviceIds'>, items: AppExtra[]): number | null => {
  let min: number | null = null;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (group.serviceIds.indexOf(item.serviceId) < 0) continue;
    const candidates: number[] = [];
    if (item.price > 0) candidates.push(item.price);
    (item.options.wineVariants || []).forEach((v) => { if (v.isAvailable && v.price > 0) candidates.push(v.price); });
    (item.options.bundleVariants || []).forEach((b) => { if (b.price > 0) candidates.push(b.price); });
    (item.options.cleaningVariants || []).forEach((v) => { if (v.price > 0) candidates.push(v.price); });
    // I servizi "scegli un articolo" costano 0 a listino: il "da" è
    // l'articolo più economico (la stessa formula di extraSelection.ts).
    const cheapestItem = cheapestItemPrice(item);
    if (cheapestItem !== null) candidates.push(cheapestItem);
    // Le fasce del listino, anche quelle che adesso non si comprano (passate,
    // troppo vicine): il "da" è il prezzo, non la disponibilità.
    listedSlots(item).forEach((s) => { if (s.price > 0) candidates.push(s.price); });
    if (item.options.transfer) {
      const tr = item.options.transfer;
      if (tr.arrivalPrice > 0) candidates.push(tr.arrivalPrice);
      (tr.stationOptions || []).forEach((s) => { if (s.price > 0) candidates.push(s.price); });
    }
    for (let j = 0; j < candidates.length; j++) {
      if (min === null || candidates[j] < min) min = candidates[j];
    }
  }
  return min;
};
